// @flow
import React, { Component } from 'react';

import Paper from 'material-ui/Paper';
import IconButton from 'material-ui/IconButton';
import AutoComplete from 'material-ui/AutoComplete';

import MenuItem from 'material-ui/MenuItem';
import Divider from 'material-ui/Divider';
import FontIcon from 'material-ui/FontIcon';

import SearchIcon from 'material-ui/svg-icons/action/search';
import CloseIcon from 'material-ui/svg-icons/navigation/close';

import { cyan500, cyan100, grey500 } from 'material-ui/styles/colors';

import { AIRSPACE_TYPE_LABELS } from 'utils/propSheet';
import debounce from 'lodash.debounce';

const GEOCODER_DEBOUNCE_INTERVAL = 200;

function getStyles({ isMobile, state }) {
  const { value, hasFocus } = state;
  const nonEmpty = value.length > 0;

  return {
    root: {
      height: 48,
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between',
      margin: 10,
      background: hasFocus ? 'white' : cyan100,
    },

    searchContainer: {
      margin: 'auto 16px',
      width: '100%',
    },

    input: {
      width: '100%',
      color: hasFocus ? 'black' : nonEmpty ? cyan500 : 'white',
    },

    inputHint: {
      color: hasFocus ? grey500 : cyan500,
      fontStyle: 'italic',
    },

    iconButtonClose: {
      style: {
        opacity: 0.54,
        transform: nonEmpty ? 'scale(1, 1)' : 'scale(0, 0)',
        transition: 'transform 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      },
      iconStyle: {
        opacity: nonEmpty ? 1 : 0,
        transition: 'opacity 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      },
    },

    iconButtonSearch: {
      style: {
        opacity: 0.54,
        transform: nonEmpty ? 'scale(0, 0)' : 'scale(1, 1)',
        transition: 'transform 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        marginRight: -48,
      },

      iconStyle: {
        opacity: nonEmpty ? 0 : 1,
        transition: 'opacity 200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      },
    },

    resultPopover: {
      marginLeft: isMobile ? -84 : 0,
      marginTop: isMobile ? 10 : 0,
      background:
        'white url(/assets/powered_by_google.png) no-repeat top 6px right 6px',
      backgroundSize: '144px 18px',
      ...(isMobile ? { width: '100vw' } : {}),
    },

    resultItem: {
      minHeight: 32,
      lineHeight: '32px',
    },

    resultInner: {
      fontSize: '.8em',
    },

    resultIcon: {
      marginTop: 4,
      marginBottom: 4,
      height: 12,
      width: 12,
    },

    resultSecondaryText: {
      fontSize: '.9em',
      color: grey500,
    },
  };
}

export default class LocationSearchField extends Component {
  state = {
    hasFocus: false,
    value: '',
  };

  componentDidMount(){
    const { browser } = this.props;
    const isMobile = browser.lessThan.medium;
    if (isMobile) {
      this.handleFocus();
    }
  }

  handleFocus() {
    this.setState({ hasFocus: true });
  }

  handleBlur() {
    this.setState({ hasFocus: false });
  }

  handleInput(e) {
    this.setState({ value: e });
    this.loadCompletions(e);
  }

  handleKeyPressed(e) {
    const {
      geocoder: { completions },
    } = this.props;
    if (e.charCode === 13) {
      const allCompletions = [
        ...completions.gmp.map(e => ({ ...e, result_type: 'gmp' })),
        ...completions.as.map(e => ({ ...e, result_type: 'as' })),
        ...completions.ap.map(e => ({ ...e, result_type: 'ap' })),
        ...completions.ac.map(e => ({ ...e, result_type: 'ac' })),
      ];

      if (allCompletions.length > 0) {
        this.props.localize({
          value: {
            props: {
              value: allCompletions[0],
            },
          },
        });
      }
    }
  }

  handleCancel() {
    this.setState({
      hasFocus: false,
      value: '',
    });
  }

  highlightQueryTerm(query, label) {
    const i = label.toLowerCase().indexOf(query.toLowerCase());
    const l = query.length;

    if (i === -1) {
      return label;
    }
    return (
      <span>
        {label.substr(0, i)}
        <strong>{label.substr(i, l)}</strong>
        {label.substr(i + l)}
      </span>
    );
  }

  renderCompletionList(styles) {
    const {
      browser,
      geocoder: { query, completions },
    } = this.props;
    const isMobile = browser.lessThan.medium;

    // Do not display "No results" when query is empty
    if (!query) {
      return [];
    }

    // Display "No results" when all result groups are empty
    if (
      ['gmp', 'as', 'ap', 'ac'].reduce(
        (sum, e) => sum + (completions[e] ? completions[e].length : 0),
        0
      ) === 0
    ) {
      return [
        {
          text: '',
          value: <MenuItem primaryText={<em>Nincs találat</em>} disabled />,
        },
      ];
    }

    const resultGroups = ['gmp', 'as', 'ap', 'ac']
      .reduce(
        (agg, e) => [
          ...agg,
          completions[e].map(r => ({
            text: r.description,
            value: (
              <MenuItem
                key={r.description}
                style={styles.resultItem}
                innerDivStyle={styles.resultInner}
                primaryText={this.highlightQueryTerm(query, r.description)}
                secondaryText={
                  isMobile ? null : (
                    <span style={styles.resultSecondaryText}>
                      {e === 'as'
                        ? AIRSPACE_TYPE_LABELS[r.airspace_type]
                        : e === 'ap'
                          ? 'Repülőtér'
                          : e === 'ac'
                            ? 'Légijármű'
                            : ''}
                    </span>
                  )
                }
                leftIcon={
                  <FontIcon
                    className="material-icons"
                    style={styles.resultIcon}
                  >
                    {e === 'gmp'
                      ? 'place'
                      : e === 'as'
                        ? 'flight'
                        : e === 'ap'
                          ? 'flight_takeoff'
                          : 'flight'}
                  </FontIcon>
                }
                value={{ ...r, result_type: e }}
              />
            ),
          })),
        ],
        {}
      )
      .filter(r => r.length);

    // Insert dividers between result groups
    return resultGroups.reduce(
      (agg, r, i) => [
        ...agg,
        ...r,
        ...(i === resultGroups.length - 1
          ? []
          : [
              {
                text: '',
                value: <Divider />,
              },
            ]),
      ],
      []
    );
  }

  componentDidUpdate = () => {
    if (!this.loadCompletions) {
      this.loadCompletions = debounce(
        this.props.loadCompletions,
        GEOCODER_DEBOUNCE_INTERVAL
      );
    }
  };

  render() {
    const { browser, localize } = this.props;
    const { hasFocus, value } = this.state;
    const isMobile = browser.lessThan.medium;
    const styles = getStyles({ isMobile, state: this.state });

    return (
      <Paper
        className="intro-tour__step-1"
        style={styles.root}
        zDepth={hasFocus ? 1 : 0}
      >
        <div style={styles.searchContainer}>
          <AutoComplete
            hintText={
              isMobile
                ? 'Keresés...'
                : 'Keressen címre, repülőtérre, légtérre vagy légijárműre...'
            }
            dataSource={this.renderCompletionList(styles)}
            searchText={value}
            onClick={() => this.handleFocus()}
            onBlur={() => this.handleBlur()}
            onUpdateInput={e => this.handleInput(e)}
            onKeyPress={e => this.handleKeyPressed(e)}
            onNewRequest={localize}
            filter={() => true}
            inputStyle={styles.input}
            hintStyle={styles.inputHint}
            listStyle={styles.resultList}
            popoverProps={{
              zDepth: isMobile ? 0 : 1,
              style: styles.resultPopover,
            }}
            underlineShow={false}
            fullWidth
            autoFocus
          />
        </div>

        <IconButton
          onClick={localize}
          iconStyle={styles.iconButtonSearch.iconStyle}
          style={styles.iconButtonSearch.style}
        >
          <SearchIcon color={hasFocus ? grey500 : cyan500} />
        </IconButton>
        <IconButton
          onClick={() => this.handleCancel()}
          iconStyle={styles.iconButtonClose.iconStyle}
          style={styles.iconButtonClose.style}
        >
          <CloseIcon color={hasFocus ? grey500 : cyan500} />
        </IconButton>
      </Paper>
    );
  }
}



// WEBPACK FOOTER //
// ./components/LocationSearchField/LocationSearchField.js